import { Component, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonTitle,
  IonToolbar,
  NavController,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom, map } from 'rxjs';
import {
  getCreateProspectDtoFormGroup,
  WorkAddressSearchResultDto,
  WorkAddressService,
  WorkCreateProspectDto,
  WorkProspectsV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { AddressAutocompleteComponent, InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { applyAddressToProspectForm } from '../../../../../../../work-office/src/app/features/akquiseobjekte/models/apply-address-to-prospect-form';

/** Erfassen (`/akquise/neu`) und Bearbeiten (`/akquise/details/:prospectId/bearbeiten`) eines Akquiseobjekts. */
@Component({
  selector: 'sfg-app-akquise-erfassen-page',
  templateUrl: './akquise-erfassen-page.component.html',
  imports: [
    AddressAutocompleteComponent,
    ButtonComponent,
    InputFieldComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class AkquiseErfassenPageComponent implements OnInit {
  private static readonly LOCATION_TIMEOUT_MS = 15000;
  private static readonly LOCATION_MAX_AGE_MS = 30000;

  private readonly prospectsV2Service = inject(WorkProspectsV2Service);
  private readonly addressService = inject(WorkAddressService);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navController = inject(NavController);

  protected readonly formGroup = getCreateProspectDtoFormGroup();
  protected readonly prospectId = signal<number | null>(null);
  protected readonly locationLoadingState = signal<LoadingAndFeedbackState>('none');
  protected readonly saveLoadingState = signal<LoadingAndFeedbackState>('none');
  /** Weitere Gebäude rund um den Standort — GPS trifft in dichten Quartieren oft das Nachbarhaus. */
  protected readonly nearbyAddresses = signal<WorkAddressSearchResultDto[]>([]);
  protected readonly selectedNearbyAddressIndex = signal<number | null>(null);

  /** Ohne Strasse, Ort oder Koordinaten lässt das Backend keinen Eintrag zu. */
  protected readonly hasAddress = toSignal(
    this.formGroup.valueChanges.pipe(
      map((value) => !!value.objectStreet?.trim() || !!value.objectCity?.trim() || (value.latitude != null && value.longitude != null)),
    ),
    { initialValue: false },
  );

  async ngOnInit(): Promise<void> {
    const prospectId = this.activatedRoute.snapshot.params['prospectId'];
    if (prospectId) {
      this.prospectId.set(Number(prospectId));
      await this.loadProspect(Number(prospectId));
    }
  }

  protected applyAddress(address: WorkAddressSearchResultDto): void {
    this.selectedNearbyAddressIndex.set(null);
    applyAddressToProspectForm(this.formGroup, address);
  }

  protected applyNearbyAddress(index: number): void {
    const address = this.nearbyAddresses()[index];
    if (!address) {
      return;
    }
    applyAddressToProspectForm(this.formGroup, address);
    this.selectedNearbyAddressIndex.set(index);
  }

  protected async useCurrentLocation(): Promise<void> {
    this.locationLoadingState.set('loading');
    let position: GeolocationPosition;
    try {
      position = await this.getCurrentPosition();
    } catch (locationException) {
      this.locationLoadingState.set('error');
      const permissionDenied = (locationException as GeolocationPositionError)?.code === 1;
      this.alertDialogService.showAlert({
        title: 'STANDORT_NICHT_VERFUEGBAR',
        message: permissionDenied ? 'STANDORT_NICHT_VERFUEGBAR_BERECHTIGUNG' : 'STANDORT_NICHT_VERFUEGBAR_HINWEIS',
        feedbackState: 'warning',
      });
      return;
    }

    try {
      const addresses = await lastValueFrom(
        this.addressService.addressV2ControllerReverseSearchAddress({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      );
      this.nearbyAddresses.set(addresses);
      if (addresses.length) {
        this.applyNearbyAddress(0);
      } else {
        // Kein Gebäude in der Nähe (Baustelle, Feld, Ausland): wenigstens den Punkt festhalten.
        this.selectedNearbyAddressIndex.set(null);
        this.formGroup.patchValue({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        this.formGroup.markAsDirty();
        this.alertDialogService.showAlert({
          title: 'STANDORT_KEINE_ADRESSE_GEFUNDEN',
          message: 'STANDORT_KEINE_ADRESSE_GEFUNDEN_HINWEIS',
          feedbackState: 'warning',
        });
      }
      this.locationLoadingState.set('success');
    } catch (reverseSearchException) {
      this.locationLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('reverseSearchException', reverseSearchException);
    }
  }

  protected async save(): Promise<void> {
    if (!this.hasAddress() || this.saveLoadingState() === 'loading') {
      return;
    }
    this.saveLoadingState.set('loading');
    const body = this.formGroup.value as WorkCreateProspectDto;
    const prospectId = this.prospectId();
    try {
      if (prospectId) {
        await lastValueFrom(this.prospectsV2Service.prospectsV2ControllerUpdate({ id: prospectId, body }));
        this.saveLoadingState.set('success');
        this.formGroup.markAsPristine();
        await this.navController.navigateBack(['/mitarbeitende/akquise/details', prospectId]);
        return;
      }
      const createdProspect = await lastValueFrom(this.prospectsV2Service.prospectsV2ControllerCreate({ body }));
      this.saveLoadingState.set('success');
      this.formGroup.markAsPristine();
      this.alertDialogService.showAlert({ title: 'ERSTELLEN_ERFOLGREICH', feedbackState: 'success' });
      // Ersetzt das Formular in der Historie: «Zurück» auf der Detailseite führt zur Liste, nicht ins leere Formular.
      await this.navController.navigateForward(['/mitarbeitende/akquise/details', createdProspect.id], { replaceUrl: true });
    } catch (saveProspectException) {
      this.saveLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('saveProspectException', saveProspectException);
    }
  }

  private async loadProspect(prospectId: number): Promise<void> {
    try {
      const prospect = await lastValueFrom(this.prospectsV2Service.prospectsV2ControllerFindOneById({ id: prospectId }));
      this.formGroup.patchValue(prospect);
      this.formGroup.markAsPristine();
    } catch (loadProspectException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProspectException', loadProspectException);
    }
  }

  /** Browser-Geolocation statt eigenem Capacitor-Plugin: die WebView fragt die Standortberechtigung selbst an. */
  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not available'));
        return;
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: AkquiseErfassenPageComponent.LOCATION_TIMEOUT_MS,
        maximumAge: AkquiseErfassenPageComponent.LOCATION_MAX_AGE_MS,
      });
    });
  }
}
